import type { ComponentProps } from 'react';
import { teamFilter } from '../use-teams';
import { formatHours, toWireDate, type ProjectMember, type Worklog } from '@forecastify/shared';
import { DataList, type DataListColumn, type DataListRow, type SortState } from '@forecastify/ui';
import { useT } from '../../i18n';
import { useCalendarLabels } from '../date-field';
import { useAvatars } from '../use-avatar';
import { listLabels } from './clients-screen';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The project's Idő tab — `markup.html` 651 and `main.jsx` 2635–2650.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * C7 deferred this tab, C11 built it, and **C12 moved its search and its filter to the server** —
 * which its own header note said would happen *"the day this list pages"*. That day is this chunk.
 *
 * ⚠️⚠️ **WHAT CHANGED, AND WHY IT IS NOT A REFACTOR.** The C11 version filtered the rows it held
 * and derived the member filter's options from them. Both were exact only because the endpoint
 * returned every row. Paged, both are wrong in ways nothing on screen would show:
 *
 *  * a search over the loaded page finds three of fifty and offers a cursor that skips the rest, so
 *    page two searches rows page one never had;
 *  * options derived from the page can only offer people who appear in it — so filtering by
 *    somebody real returns nothing and reads as "they logged nothing".
 *
 * So the search box and the filter are **query parameters**, and the options come from the
 * project's own membership list, which is the authoritative set of people who could have logged.
 *
 * ⚠️ **The footer's total is the PROJECT's total, from SQL, not a sum of what is loaded.**
 * `state-rules.md` §5: a figure derived from a partial fetch is never presented as a total.
 */
export function ProjectTimeTab({
  entries,
  taskChoices,
  dateRange,
  taskId,
  hoursRange,
  onDateRange,
  onTask,
  onHoursRange,
  members,
  trackedHours,
  page,
  sort,
  onSortChange,
  search,
  memberId,
  teams = [],
  teamId = '',
  onTeam,
  serverError,
  onSearch,
  onMember,
  loading,
}: {
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean | undefined;
  /** One page, newest day first — the SERVER's page (30 Oct 2026). */
  readonly entries: readonly Worklog[];
  /**
   * 30 Oct 2026 · **paged on the server, 50 a page, `[<] [selector] [>]`.** It used to load fifty
   * with a cursor, page those fifty in the browser, AND offer *Load more* to fetch the next — two
   * pagers for one list (the product owner: *"a pagination should be enough"*).
   */
  readonly page?: ComponentProps<typeof DataList>['page'];
  /** The server's order — `WORKLOG_SORT`'s keys. Sorting a page in hand would sort fifty rows. */
  readonly sort: readonly SortState[];
  readonly onSortChange: (next: readonly SortState[]) => void;
  /** Everybody on the project, ever — the filter's options. */
  readonly members: readonly ProjectMember[];
  /** 30 Oct 2026 · every task on the project — the task filter's options. */
  readonly taskChoices: readonly { id: string; key: string; title: string }[];
  /** The project's total, all time, summed in SQL. */
  readonly trackedHours: string;
  readonly search: string;
  readonly memberId: string;
  /** R-60 · the team filter, server-side like the member one. */
  readonly teams?: readonly { readonly id: string; readonly name: string }[];
  readonly teamId?: string;
  readonly onTeam?: (value: string) => void;
  /**
   * ┌──────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THREE MORE FILTERS — round 5 item 13.                                  │
   * └──────────────────────────────────────────────────────────────────┘
   *
   * *"Kevés a szűrő, kell még dátum, feladat, rögzített óra szűrő."* The tab had one, over people.
   *
   * ⚠️ **Both ranges are ONE STRING, `from..to`** — `list-toolbar.tsx`'s own shape, so the chip,
   * the active test and *clear all* need no second branch. The route splits them exactly once,
   * where they become query parameters.
   *
   * ⚠️ **All three narrow on the SERVER**, like the member filter beside them: the tab shows one
   * page of a project's hours, and a filter applied to the page in hand would answer about fifty
   * rows while claiming to answer about the project.
   */
  readonly dateRange: string;
  readonly taskId: string;
  readonly hoursRange: string;
  readonly serverError: string | null;
  readonly onSearch: (value: string) => void;
  readonly onMember: (value: string) => void;
  readonly onDateRange: (value: string) => void;
  readonly onTask: (value: string) => void;
  readonly onHoursRange: (value: string) => void;
}) {
  const t = useT();

  /* ⚠️ One request per distinct picture for the whole list — see `use-avatar.ts`. */
  /* ⚠️ The app's one translated calendar vocabulary — `ListToolbar` lives in `@forecastify/ui`
     and cannot reach `useT`, so a date bound takes it as a prop. `tasks-screen.tsx` does the same. */
  const calendar = useCalendarLabels();

  /**
   * ⚠️ **What a range CHIP says** — `2026-01-01..` is meaningless as a label, so the toolbar
   * asks the caller. One `t()` per branch with a literal key, rule 7's shape.
   *
   * ⚠️ Hours go through `formatHours` like every other hour in the product; a date is shown as
   * it was typed.
   */
  const rangeChip = (value: string, unit: 'date' | 'hours'): string => {
    const [from = '', to = ''] = value.split('..');
    const show = (bound: string): string => (unit === 'hours' ? formatHours(Number(bound)) : bound);
    if (from !== '' && to !== '') return t('list.rangeBoth', { from: show(from), to: show(to) });
    if (from !== '') return t('list.rangeFromOnly', { from: show(from) });
    return t('list.rangeToOnly', { to: show(to) });
  };

  const avatars = useAvatars(entries.map((entry) => entry.memberAvatarUrl));

  /* 30 Oct 2026 · in the server's order — the page arrives sorted. */
  const rows: DataListRow[] = entries.map((entry) => ({
    id: entry.id,
    cells: {
      date: { text: entry.workedOn, mono: true, muted: true },
      task: { text: entry.taskKey, mono: true },
      /* ⚠️ An em-dash for an absent note, never a blank cell — the kit's rule, and a blank row here
       * reads as a rendering fault rather than as "nobody wrote anything". */
      note: { text: entry.note ?? '–', muted: entry.note === null },
      who: {
        text: entry.memberName,
        muted: true,
        avatar: {
          initials: initialsOf(entry.memberName),
          ...(entry.memberAvatarSeed === null ? {} : { seed: entry.memberAvatarSeed }),
          title: entry.memberName,
          src: avatars.get(entry.memberAvatarUrl ?? ''),
        },
      },
      /* ⚠️ `mono` is what gives tabular numerals. Right alignment is the COLUMN's property. */
      hours: { text: formatHours(entry.hours), mono: true, bold: true },
    },
  }));

  const columns: DataListColumn[] = [
    /* 30 Oct 2026 · the sort keys are `WORKLOG_SORT`'s, because the server orders the pages. */
    { key: 'date', label: t('time.column.date'), width: 120, sortKey: 'worked_on' },
    { key: 'task', label: t('time.column.task'), width: 120, sortKey: 'task' },
    { key: 'note', label: t('time.column.note'), sortKey: 'note' },
    { key: 'who', label: t('time.column.member'), width: 180, sortKey: 'member' },
    {
      key: 'hours',
      label: t('time.column.hours'),
      width: 100,
      align: 'right',
      sortKey: 'hours',
    },
  ];

  /**
   * ⚠️ **The footer says what is loaded AND what the project's total is, as two facts.** One number
   * doing both jobs is how "50 entries · 46:00" comes to describe a set nobody can see. When
   * everything is loaded and nothing is filtered the two agree and the sentence is the simple one.
   */
  const filtered = search.trim() !== '' || memberId !== '' || teamId !== '';
  const count = page?.total ?? entries.length;
  const footer = filtered
    ? t('project.time.footerPartial', { count, hours: formatHours(trackedHours) })
    : t('project.time.footer', { count, hours: formatHours(trackedHours) });

  return (
    /* ⚠️ Edge to edge — see `clients-screen.tsx`. The banner keeps its own inset, because a
       sentence hard against the window edge is a different thing from a table that fills it. */
    <div className="flex min-h-0 flex-1 flex-col">
      {serverError !== null && (
        <p
          role="alert"
          className="border-destructive/40 bg-destructive/10 text-destructive mx-5 mt-3 mb-3 rounded-md border px-3 py-2 text-[12.5px]"
        >
          {serverError}
        </p>
      )}

      <DataList
        columnsKey="project-time"
        card={{
          title: 'task',
          context: 'date',
          defaults: { avatar: 'who', details: ['hours', 'note'] },
        }}
        /* ⚠️ No bulk action exists for a project's worklogs, so no checkboxes either. */
        selectable={false}
        isLoading={loading === true}
        columns={columns}

        rows={rows}
        {...(page === undefined ? {} : { page })}
        sort={sort}
        onSortChange={onSortChange}
        search={search}
        onSearchChange={onSearch}
        filters={[
          {
            key: 'member',
            label: t('time.column.member'),
            value: memberId,
            onChange: onMember,
            options: [
              { value: '', label: t('common.all') },
              /* ⚠️ From the membership list, not from the loaded rows. A filter that can only offer
               * people who appear on the current page returns nothing for everybody else. */
              ...members.map((member) => ({
                value: member.memberId,
                label: member.displayName,
              })),
            ],
          },
          ...(onTeam === undefined ? [] : teamFilter(t, teams, teamId, onTeam)),
          {
            key: 'task',
            label: t('time.column.task'),
            value: taskId,
            onChange: onTask,
            options: [
              { value: '', label: t('common.all') },
              /* ⚠️⚠️ **THE PROJECT'S TASKS, NOT THE LOADED ROWS (30 Oct 2026).** The options were
                 the tasks the loaded page mentioned — fifty entries' worth, and a single task once
                 the filter itself was set, so it could not be changed to another. */
              ...taskChoices
                .map((one) => ({ value: one.id, label: `${one.key} · ${one.title}` }))
                .sort((a, b) => a.label.localeCompare(b.label)),
            ],
          },
          {
            key: 'date',
            label: t('time.column.date'),
            value: dateRange,
            onChange: onDateRange,
            kind: 'range' as const,
            input: 'date' as const,
            calendar,
            today: toWireDate(new Date()),
            options: [],
            display: (value: string) => rangeChip(value, 'date'),
          },
          {
            key: 'hours',
            label: t('time.column.hours'),
            value: hoursRange,
            onChange: onHoursRange,
            kind: 'range' as const,
            input: 'number' as const,
            options: [],
            display: (value: string) => rangeChip(value, 'hours'),
          },
        ]}
        empty={{
          /* ⚠️ `body`, not `description` — the `.d.ts` says `description` and the implementation
           * reads `body`. `design-system-findings.md` records the divergence. */
          icon: 'clock',
          title: filtered ? t('project.time.noMatch') : t('project.time.empty'),
          body: filtered ? t('project.time.noMatchNote') : t('project.time.emptyNote'),
        }}
        footerNote={footer}
        labels={listLabels(t)}
      />
    </div>
  );
}

/**
 * ⚠️ **Two initials, taken from the first and last word.** `AvatarStack` elsewhere does the same;
 * a single letter collides across a team of eight, and a name of one word has one initial rather
 * than a repeated letter.
 */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}
